export enum ServicingAuditCheck {
  FacilityFee = 'facilityFee',
  TermLoans = 'termLoans',
  Amendments = 'amendments',
  MonthEndFees = 'monthEndFees',
  OveradvanceFee = 'overadvanceFee',
  Maturity = 'maturity',
  LoanBalance = 'loanBalance',
}

export enum ServicingAuditStatus {
  Pass = 'pass',
  Warning = 'warning',
  Fail = 'fail',
  Acknowledged = 'acknowledged',
}

export type ServicingAuditSeverity = ServicingAuditStatus.Warning | ServicingAuditStatus.Fail

export const SERVICING_AUDIT_STATUS_LABELS: Record<ServicingAuditStatus, string> = {
  [ServicingAuditStatus.Pass]: 'Passing',
  [ServicingAuditStatus.Warning]: 'Warning',
  [ServicingAuditStatus.Fail]: 'Failing',
  [ServicingAuditStatus.Acknowledged]: 'Marked as intended',
}

export const isOpenIssueStatus = (status: ServicingAuditStatus): boolean =>
  status === ServicingAuditStatus.Fail || status === ServicingAuditStatus.Warning

export const SERVICING_AUDIT_CHECK_LABELS: Record<ServicingAuditCheck, string> = {
  [ServicingAuditCheck.FacilityFee]: 'Facility Fee',
  [ServicingAuditCheck.TermLoans]: 'Term Loans',
  [ServicingAuditCheck.Amendments]: 'Amendments',
  [ServicingAuditCheck.MonthEndFees]: 'Month-End Fees',
  [ServicingAuditCheck.OveradvanceFee]: 'Overadvance Fee',
  [ServicingAuditCheck.Maturity]: 'Maturity',
  [ServicingAuditCheck.LoanBalance]: 'Loan Balance',
}

export const SERVICING_AUDIT_CHECK_DESCRIPTIONS: Record<ServicingAuditCheck, string> = {
  [ServicingAuditCheck.FacilityFee]:
    'Each facility fee (line and term loan) posted on its due date, only once, and the next due date moved forward. Shows the next due date.',
  [ServicingAuditCheck.TermLoans]:
    'Term loans activated on their closing date, paid off at maturity, and charged interest (and principal when amortizing) at the last locked month-end. Shows the earliest maturity.',
  [ServicingAuditCheck.Amendments]:
    'Scheduled amendments took effect on their effective date. Shows the next scheduled amendment, or the last one applied.',
  [ServicingAuditCheck.MonthEndFees]:
    'Interest and lockbox fees posted at the last locked month-end where the terms require them. Shows the total month-end fees.',
  [ServicingAuditCheck.OveradvanceFee]:
    'Overadvances started in the last year had their fee posted. Shows the latest overadvance fee.',
  [ServicingAuditCheck.Maturity]:
    'Auto-renewing facilities were renewed before their maturity date passed. Shows the maturity date.',
  [ServicingAuditCheck.LoanBalance]:
    "Today's loan balance is calculated, matches account activity, and agrees with the crystalized balance. Shows today's balance.",
}

/** The figure a cell shows, plus supporting lines for its tooltip. */
export interface IServicingAuditCellSummary {
  label: string
  /** What the column sorts by: an ISO date or an amount. */
  sortValue: string | number
  details: string[]
}

export const SERVICING_AUDIT_SORT_FIELDS = [
  'clientName',
  'status',
  ...Object.values(ServicingAuditCheck),
] as const

export type ServicingAuditSortField = (typeof SERVICING_AUDIT_SORT_FIELDS)[number]

/** Counted across every client, before filters, so the card reads the same whatever is shown. */
export interface IServicingAuditCounts {
  clientsWithIssues: number
  totalClients: number
}

/** One problem found by a check — `issueKey` is stable across runs so it can be tracked and acknowledged. */
export interface IServicingAuditFinding {
  issueKey: string
  check: ServicingAuditCheck
  clientName: string
  recordDate: string | null
  severity: ServicingAuditSeverity
  message: string
}

export interface IServicingAuditIssue extends IServicingAuditFinding {
  firstSeenAt: string | null
  acknowledgedAt: string | null
  acknowledgedByName: string | null
  acknowledgementNote: string | null
}

export interface IServicingAuditCheckResult {
  status: ServicingAuditStatus
  issues: IServicingAuditIssue[]
  /** Null when the client has nothing this check applies to (e.g. no term loans). */
  summary: IServicingAuditCellSummary | null
}

export interface IServicingAuditClientRow {
  clientId: string
  clientName: string
  status: ServicingAuditStatus
  checks: Record<ServicingAuditCheck, IServicingAuditCheckResult>
}

export enum ServicingAuditPortfolioCheck {
  PrimeRate = 'primeRate',
  MonthEnd = 'monthEnd',
  Crystalization = 'crystalization',
}

export interface IServicingAuditPortfolioResult {
  key: ServicingAuditPortfolioCheck
  title: string
  status: ServicingAuditStatus
  message: string
}

export type IServicingAuditIssueAcknowledgement = Pick<
  IServicingAuditIssue,
  'issueKey' | 'acknowledgedAt' | 'acknowledgedByName' | 'acknowledgementNote'
>

export interface IAcknowledgeServicingAuditIssueParams {
  issueKey: string
  acknowledged: boolean
  note?: string
}

export interface IServicingAuditResponse {
  checkedAt: string
  portfolio: IServicingAuditPortfolioResult[]
  counts: IServicingAuditCounts
  clients: IServicingAuditClientRow[]
}
